import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import axios from 'axios';
import AuthImg from '../components/devreport/presentation/AuthImg';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const PlayerMedia = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [athleteId, setAthleteId] = useState(null);
  const [galleryStructure, setGalleryStructure] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [highlights, setHighlights] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('gallery');
  
  // Selected year/month for viewing
  const [selectedYear, setSelectedYear] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(null);
  
  // Dialog states
  const [viewPhotoDialog, setViewPhotoDialog] = useState(false);
  const [viewHighlightDialog, setViewHighlightDialog] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [selectedHighlight, setSelectedHighlight] = useState(null);

  useEffect(() => {
    if (!['Player', 'Parent'].includes(user?.role)) {
      navigate('/dashboard');
      return;
    }
    fetchUserAndMedia();
  }, [user, navigate]);

  const fetchUserAndMedia = async () => {
    setLoading(true);
    try {
      // Get user's linked athlete
      const meRes = await axios.get(`${API}/auth/me`);
      const linkedAthleteId = meRes.data.athlete_id;
      
      if (!linkedAthleteId) {
        setLoading(false);
        return;
      }
      
      setAthleteId(linkedAthleteId);
      
      // Fetch media data
      const [structureRes, highlightsRes] = await Promise.all([
        axios.get(`${API}/media/photos/${linkedAthleteId}/structure`),
        axios.get(`${API}/media/highlights/${linkedAthleteId}`)
      ]);
      
      setGalleryStructure(structureRes.data);
      setHighlights(highlightsRes.data);
      
      // If structure exists, select the first year/month
      if (structureRes.data.length > 0) {
        const firstYear = structureRes.data[0];
        setSelectedYear(firstYear.year);
        if (firstYear.months.length > 0) {
          setSelectedMonth(firstYear.months[0].month);
          const photosRes = await axios.get(`${API}/media/photos/${linkedAthleteId}?year=${firstYear.year}&month=${firstYear.months[0].month}`);
          setPhotos(photosRes.data);
        }
      }
    } catch (error) {
      toast.error('Failed to load media');
    } finally {
      setLoading(false);
    }
  };

  const fetchPhotosForMonth = async (year, month) => {
    if (!athleteId) return;
    
    try {
      const response = await axios.get(`${API}/media/photos/${athleteId}?year=${year}&month=${month}`);
      setPhotos(response.data);
      setSelectedYear(year);
      setSelectedMonth(month);
    } catch (error) {
      toast.error('Failed to load photos');
    }
  };

  const getPlatformIcon = (platform) => {
    const icons = {
      youtube: '🎬',
      vimeo: '🎥',
      google_drive: '📁',
      veo: '⚽',
      other: '🔗'
    };
    return icons[platform] || '🔗';
  };

  if (!['Player', 'Parent'].includes(user?.role)) return null;

  return (
    <Layout>
      <div className="p-6 lg:p-8">
        <div className="mb-6">
          <h2 className="text-3xl font-bold text-gray-900 dark:text-gray-100" style={{ fontFamily: 'Manrope, sans-serif' }}>
            {user?.role === 'Parent' ? "Your Child's" : 'My'} Media & Highlights
          </h2>
          <p className="text-gray-600 dark:text-gray-500 mt-1">Training photos and match highlights</p>
        </div>

        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
          </div>
        ) : !athleteId ? (
          <Card>
            <CardContent className="py-12 text-center">
              <p className="text-gray-500 dark:text-gray-500">Your account is not linked to an athlete profile yet.</p>
            </CardContent>
          </Card>
        ) : (
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="mb-6">
              <TabsTrigger value="gallery" className="gap-2">
                📷 Training Gallery
              </TabsTrigger>
              <TabsTrigger value="highlights" className="gap-2">
                🎬 Match Highlights
              </TabsTrigger>
            </TabsList>

            {/* Training Gallery Tab */}
            <TabsContent value="gallery">
              <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
                {/* Year/Month Navigation */}
                <Card className="lg:col-span-1">
                  <CardHeader>
                    <CardTitle className="text-sm">Browse by Date</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {galleryStructure.length === 0 ? (
                      <p className="text-gray-500 dark:text-gray-500 text-sm">No photos uploaded yet</p>
                    ) : (
                      <div className="space-y-4">
                        {galleryStructure.map(yearData => (
                          <div key={yearData.year}>
                            <div className="font-medium text-gray-900 dark:text-gray-100 mb-2 flex items-center gap-2">
                              📅 {yearData.year}
                              <Badge variant="outline" className="text-xs">
                                {yearData.total_photos} photos
                              </Badge>
                            </div>
                            <div className="space-y-1 ml-4">
                              {yearData.months.map(monthData => (
                                <button
                                  key={monthData.month}
                                  className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                                    selectedYear === yearData.year && selectedMonth === monthData.month
                                      ? 'bg-emerald-100 text-emerald-800 font-medium'
                                      : 'hover:bg-gray-100 dark:bg-gray-700'
                                  }`}
                                  onClick={() => fetchPhotosForMonth(yearData.year, monthData.month)}
                                >
                                  {monthData.name}
                                  <span className="text-gray-500 dark:text-gray-500 ml-2">({monthData.count})</span>
                                </button>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Photo Grid */}
                <Card className="lg:col-span-3">
                  <CardHeader>
                    <CardTitle>
                      {selectedYear && selectedMonth 
                        ? `${MONTH_NAMES[selectedMonth - 1]} ${selectedYear}`
                        : 'Select a month to view photos'}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {photos.length === 0 ? (
                      <div className="text-center py-12">
                        <p className="text-gray-500 dark:text-gray-500">
                          {selectedYear ? 'No photos in this month' : 'Select a month from the sidebar'}
                        </p>
                      </div>
                    ) : (
                      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                        {photos.map(photo => (
                          <div 
                            key={photo.id}
                            className="relative group aspect-square rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-700 cursor-pointer"
                            onClick={() => {
                              setSelectedPhoto(photo);
                              setViewPhotoDialog(true);
                            }}
                          >
                            <AuthImg
                              src={photo.file_url}
                              alt={photo.caption || 'Training photo'}
                              className="w-full h-full object-cover transition-transform group-hover:scale-105"
                              testId={`player-photo-image-${photo.id}`}
                            />
                            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors" />
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* Match Highlights Tab */}
            <TabsContent value="highlights">
              {highlights.length === 0 ? (
                <Card>
                  <CardContent className="py-12 text-center">
                    <div className="w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
                      <span className="text-3xl">🎬</span>
                    </div>
                    <p className="text-gray-500 dark:text-gray-500">No video highlights added yet</p>
                    <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">Check back later for match highlights!</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {highlights.map(highlight => (
                    <Card key={highlight.id} className="overflow-hidden cursor-pointer" onClick={() => {
                      setSelectedHighlight(highlight);
                      setViewHighlightDialog(true);
                    }}>
                      <div className="aspect-video bg-gray-900 relative group">
                        {highlight.thumbnail_url ? (
                          <img 
                            src={highlight.thumbnail_url}
                            alt={highlight.title}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-6xl text-gray-600 dark:text-gray-500">
                            {getPlatformIcon(highlight.platform)}
                          </div>
                        )}
                        <div className="absolute inset-0 flex items-center justify-center">
                          <div className="w-16 h-16 bg-white dark:bg-gray-800/90 rounded-full flex items-center justify-center shadow-lg opacity-80 group-hover:opacity-100 transition-opacity">
                            <span className="text-2xl ml-1">▶</span>
                          </div>
                        </div>
                        <Badge className="absolute top-2 right-2 bg-black/70">
                          {highlight.platform}
                        </Badge>
                      </div>
                      <CardContent className="p-4">
                        <h3 className="font-medium text-gray-900 dark:text-gray-100 mb-1">{highlight.title}</h3>
                        {highlight.competition && (
                          <p className="text-sm text-gray-600 dark:text-gray-500">
                            {highlight.competition} {highlight.opponent && `vs ${highlight.opponent}`}
                          </p>
                        )}
                        {highlight.match_date && (
                          <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">{highlight.match_date}</p>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>
        )}

        {/* View Photo Dialog (Lightbox) */}
        <Dialog open={viewPhotoDialog} onOpenChange={setViewPhotoDialog}>
          <DialogContent className="max-w-4xl">
            {selectedPhoto && (
              <div>
                <AuthImg
                  src={selectedPhoto.file_url}
                  alt={selectedPhoto.caption || 'Training photo'}
                  className="w-full rounded-lg"
                  testId="player-photo-lightbox-image"
                />
                {selectedPhoto.caption && (
                  <p className="mt-4 text-gray-700 dark:text-gray-300">{selectedPhoto.caption}</p>
                )}
                <p className="text-sm text-gray-500 dark:text-gray-500 mt-2">
                  {new Date(selectedPhoto.uploaded_at).toLocaleDateString()}
                </p>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* View Highlight Dialog (Video Player) */}
        <Dialog open={viewHighlightDialog} onOpenChange={setViewHighlightDialog}>
          <DialogContent className="max-w-4xl">
            <DialogHeader>
              <DialogTitle>{selectedHighlight?.title}</DialogTitle>
              {selectedHighlight?.competition && (
                <DialogDescription>
                  {selectedHighlight.competition} {selectedHighlight.opponent && `vs ${selectedHighlight.opponent}`}
                </DialogDescription>
              )}
            </DialogHeader>
            
            {selectedHighlight && (
              <div className="space-y-4">
                <div className="aspect-video bg-black rounded-lg overflow-hidden">
                  {selectedHighlight.embed_url ? (
                    <iframe
                      src={selectedHighlight.embed_url}
                      className="w-full h-full"
                      allowFullScreen
                      allow="autoplay; encrypted-media"
                      title={selectedHighlight.title}
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-white">
                      <a 
                        href={selectedHighlight.video_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-emerald-400 hover:underline"
                      >
                        Open video in new tab →
                      </a>
                    </div>
                  )}
                </div>
                
                {selectedHighlight.description && (
                  <p className="text-gray-700 dark:text-gray-300">{selectedHighlight.description}</p>
                )}
                
                {selectedHighlight.match_date && (
                  <p className="text-sm text-gray-500 dark:text-gray-500">Date: {selectedHighlight.match_date}</p>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default PlayerMedia;
